import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';
import { facilitiesService } from '../../services/facilities.service';
import { antennasService } from '../../services/antennas.service';

const FACILITY_TYPES = ['Headquarters','Depot','Warehouse','Store','Factory','Office','Data Center'];
const ANTENNA_LOCS   = ['Main Entrance','Exit Gate','Zone A','Zone B','Zone C','Loading Bay','Server Room','Storage Room','Office Floor','Receiving Dock'];

// Antenna is only truly online if last_ping was within 2 minutes
function isAntennaReallyOnline(ant) {
  if (ant.status !== 'Online') return false;
  if (!ant.last_ping) return false;
  const age = Date.now() - new Date(ant.last_ping).getTime();
  return age < 2 * 60 * 1000;
}

function pingAge(last_ping, now = Date.now()) {
  if (!last_ping) return 'Never';
  const sec = Math.floor((now - new Date(last_ping).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec/60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec/3600)}h ago`;
  return `${Math.floor(sec/86400)}d ago`;
}

function Chip({ label }) {
  const c = {
    'Active':          '#00e5a0',
    'Online':          '#00e5a0',
    'Available':       '#00e5a0',
    'In Use':          '#5b9cf6',
    'Under Maintenance': '#f5a623',
    'In Transit':      '#b89de8',
    'Decommissioned':  '#7090a0',
    'Inactive':        '#e05757',
    'Offline':         '#e05757',
  }[label] || '#7090a0';
  return <span style={{ background:c+'18',border:`1px solid ${c}30`,color:c,borderRadius:6,padding:'2px 9px',fontSize:11,fontWeight:700 }}>{label}</span>;
}
function Lbl({ children }) { return <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',fontWeight:600,marginBottom:6 }}>{children}</div>; }
function Modal({ title, sub, onClose, children, wide }) {
  return <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.88)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{ background:'#090e15',border:'1px solid #1a2a3a',borderRadius:14,width:'100%',maxWidth:wide?820:640,maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column' }}>
      <div style={{ padding:'20px 24px',borderBottom:'1px solid #1a2a3a',flexShrink:0 }}>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2' }}>{title}</div>
        {sub&&<div style={{ fontSize:11,color:'#7090a0',marginTop:3,fontFamily:"'IBM Plex Mono',monospace" }}>{sub}</div>}
      </div>
      <div style={{ padding:24,overflowY:'auto',flex:1 }}>{children}</div>
    </div>
  </div>;
}

export default function FacilitiesPage() {
  const { facilities, setFacilities, antennas, setAntennas, assets, perms, showToast, profile } = useApp();
  const [modal,setModal]       = useState(null);
  const [facSearch, setFacSearch]           = useState('');
  const [facSort, setFacSort]               = useState('name_asc');
  const [facStatusFilter, setFacStatusFilter] = useState('all');
  const [detail,setDetail]       = useState(null);
  const [detailTab,setDetailTab] = useState('info');
  const [antModal,setAntModal]   = useState(null);
  const [antForm,setAntForm]     = useState({});
  const [form,setForm]         = useState({});
  const [saving,setSaving]     = useState(false);
  const [bridgeReaders,setBridgeReaders] = useState([]);  // from RFID bridge
  const [bridgeUrl]   = useState(() => localStorage.getItem('rfid_bridge_url') || 'http://localhost:3500');
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 10000); return () => clearInterval(t); }, []);

  // Fetch bridge readers when antenna tab opens
  const fetchBridgeReaders = async () => {
    if (!localStorage.getItem('rfid_bridge_url')) { setBridgeReaders([]); return; }
    try {
      const res  = await fetch(`${bridgeUrl}/api/status`, { signal: AbortSignal.timeout(3000) });
      const data = await res.json();
      setBridgeReaders(data.readers || []);
    } catch(e) { setBridgeReaders([]); }
  };

  const saveAdd = async () => {
    if(!form.name||!form.code||saving) return;
    setSaving(true);
    try {
      const fac = await facilitiesService.create({ ...form, antennas_count: parseInt(form.antennas_count)||2, org_id: profile?.org_id });
      const antCount = parseInt(form.antennas_count)||2;
      const newAnts = await Promise.all(
        Array.from({length:antCount},(_,i) =>
          antennasService.create({ facility_id:fac.id, facility_name:fac.name, org_id:profile?.org_id, antenna_port:i+1, antenna_name:`ANT${i+1}`, location:ANTENNA_LOCS[i]||`Zone ${i+1}`, status:'Offline', reads:0 })
        )
      );
      setFacilities(prev=>[...prev,fac]);
      setAntennas(prev=>[...prev,...newAnts]);
      showToast('✓ Facility added');
      setModal(null);
    } catch(err){ showToast('❌ '+err.message,'error'); }
    setSaving(false);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const updated = await facilitiesService.update(modal.id, form);
      setFacilities(prev=>prev.map(f=>f.id===modal.id?{...f,...updated}:f));
      setAntennas(prev=>prev.map(a=>String(a.facility_id)===String(modal.id)?{...a,facility_name:form.name}:a));
      showToast('✓ Facility updated');
      setModal(null);
    } catch(err){ showToast('❌ '+err.message,'error'); }
    setSaving(false);
  };

  const deleteFacility = async (fac) => {
    if(assets.some(a=>a.facility_id===fac.id)){ showToast('⚠ Cannot delete — facility has assets assigned','error'); return; }
    try {
      await facilitiesService.delete(fac.id);
      setFacilities(prev=>prev.filter(f=>f.id!==fac.id));
      setAntennas(prev=>prev.filter(a=>String(a.facility_id)!==String(fac.id)));
      setDetail(null);
      showToast('✓ Facility deleted');
    } catch(err){ showToast('❌ '+err.message,'error'); }
  };

  const saveAntenna = async () => {
    if (!antForm.location) return;
    setSaving(true);
    try {
      if (antModal === 'add') {
        const newAnt = await antennasService.create({
          org_id:        profile?.org_id,
          facility_id:   antForm.facility_id,
          facility_name: antForm.facility_name,
          location:      antForm.location,
          antenna_name:  antForm.antenna_name || `ANT${antForm.antennaPort || 1}`,
          status:        antForm.status || 'Online',
          reads:         0,
          bridge_ip:     antForm.bridgeIp || null,
          bridge_port:   antForm.bridgePort || 9090,
          antenna_port:  antForm.antennaPort || 1,
        });
        setAntennas(prev => [...prev, newAnt]);
        showToast('✓ Antenna added');
      } else {
        await antennasService.update(antModal.id, {
          location:     antForm.location,
          antenna_name: antForm.antenna_name || `ANT${antForm.antennaPort || 1}`,
          status:       antForm.status,
          bridge_ip:    antForm.bridgeIp || null,
          bridge_port:  antForm.bridgePort || 9090,
          antenna_port: antForm.antennaPort || 1,
        });
        setAntennas(prev => prev.map(a => a.id === antModal.id ? { ...a, ...antForm } : a));
        showToast('✓ Antenna updated');
      }
      setAntModal(null);
    } catch(err){ showToast('❌ '+err.message,'error'); }
    setSaving(false);
  };

  const deleteAntenna = async (ant) => {
    try {
      await antennasService.delete(ant.id);
      setAntennas(prev => prev.filter(a => a.id !== ant.id));
      showToast('✓ Antenna removed');
    } catch(err){ showToast('❌ '+err.message,'error'); }
  };

  const filteredFacs = useMemo(() =>
    facilities
      .filter(f => {
        if (facStatusFilter !== 'all' && f.status !== facStatusFilter) return false;
        if (!facSearch) return true;
        const q = facSearch.toLowerCase();
        return (f.name||'').toLowerCase().includes(q) ||
               (f.code||'').toLowerCase().includes(q) ||
               (f.location||'').toLowerCase().includes(q);
      })
      .sort((a,b) => {
        const aA = assets.filter(x=>x.currentFacility===a.id||x.facility_id===a.id).length;
        const bA = assets.filter(x=>x.currentFacility===b.id||x.facility_id===b.id).length;
        const aO = antennas.filter(x=>String(x.facility_id)===String(a.id)&&isAntennaReallyOnline(x)).length;
        const bO = antennas.filter(x=>String(x.facility_id)===String(b.id)&&isAntennaReallyOnline(x)).length;
        if (facSort==='name_asc')    return (a.name||'').localeCompare(b.name||'');
        if (facSort==='name_desc')   return (b.name||'').localeCompare(a.name||'');
        if (facSort==='assets_desc') return bA-aA;
        if (facSort==='online_desc') return bO-aO;
        if (facSort==='status')      return (a.status||'').localeCompare(b.status||'');
        return 0;
      })
  , [facilities, assets, antennas, facSearch, facSort, facStatusFilter]);

  return <div>
    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24 }}>
      <div>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:'#e8edf2' }}>Facilities Management</div>
        <div style={{ fontSize:12,color:'#7090a0',marginTop:4 }}>{facilities.length} facilities · {antennas.filter(isAntennaReallyOnline).length}/{antennas.length} antennas online</div>
      </div>
      {perms.canManageFacilities && <button className="btn" style={{ background:'#00e5a0',color:'#060b10' }} onClick={()=>{setForm({name:'',code:'',location:'',type:'Warehouse',manager:'',contact:'',status:'Active',antennas_count:2});setModal('add');}}>+ Add Facility</button>}
    </div>

    <div style={{ display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20 }}>
      {[{label:'Total Facilities',value:facilities.length,sub:'All locations',icon:'🏭',c:'#00e5a0'},{label:'Active',value:facilities.filter(f=>f.status==='Active').length,sub:'Operating',icon:'✅',c:'#00e5a0'},{label:'Total Antennas',value:antennas.length,sub:`${antennas.filter(isAntennaReallyOnline).length} online`,icon:'📡',c:'#5b9cf6'},{label:'Total Assets',value:assets.length,sub:'All facilities',icon:'📦',c:'#b89de8'},{label:'Assets Not In Use',value:assets.filter(a=>a.status==='Available').length,sub:'Available now',icon:'🟢',c:'#f5a623'}].map(s=>(
        <div key={s.label} style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,padding:20 }}>
          <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:10 }}>
            <div style={{ width:36,height:36,borderRadius:9,background:s.c+'18',border:`1px solid ${s.c}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18 }}>{s.icon}</div>
            <div style={{ fontSize:10,color:'#7090a0',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.6px' }}>{s.label}</div>
          </div>
          <div style={{ fontSize:28,fontWeight:800,color:'#e8edf2',fontFamily:"'Syne',sans-serif" }}>{s.value}</div>
          <div style={{ fontSize:11,color:'#a0b4c0',marginTop:4 }}>{s.sub}</div>
        </div>
      ))}
    </div>

    {/* Search + Sort */}
    <div style={{ display:'flex',gap:10,marginBottom:14,flexWrap:'wrap' }}>
      <div style={{ flex:1,minWidth:180,position:'relative' }}>
        <span style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'#7090a0',pointerEvents:'none' }}>🔍</span>
        <input className="inp" style={{ paddingLeft:32,width:'100%',boxSizing:'border-box' }}
          placeholder="Search facilities..." value={facSearch} onChange={e=>setFacSearch(e.target.value)}/>
      </div>
      <select className="sel" value={facSort} onChange={e=>setFacSort(e.target.value)}>
        <option value="name_asc">Name A→Z</option>
        <option value="name_desc">Name Z→A</option>
        <option value="assets_desc">Most Assets</option>
        <option value="online_desc">Most Online</option>
        <option value="status">Status</option>
      </select>
      <select className="sel" value={facStatusFilter} onChange={e=>setFacStatusFilter(e.target.value)}>
        <option value="all">All Status</option>
        <option value="Active">Active</option>
        <option value="Inactive">Inactive</option>
      </select>
    </div>

    <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(310px,1fr))',gap:14 }}>
      {filteredFacs.map(fac=>{
        const facAssets  = assets.filter(a=>a.currentFacility===fac.id||a.facility_id===fac.id);
        const facAnts    = antennas.filter(a=>String(a.facility_id)===String(fac.id));
        const onlineAnts = facAnts.filter(isAntennaReallyOnline).length;
        const available  = facAssets.filter(a=>a.status==='Available').length;
        const pct        = facAnts.length ? Math.round((onlineAnts/facAnts.length)*100) : 0;
        return (
          <div key={fac.id} style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,padding:20,cursor:'pointer',transition:'border-color .15s' }}
            onMouseEnter={e=>e.currentTarget.style.borderColor='#00e5a040'}
            onMouseLeave={e=>e.currentTarget.style.borderColor='#1a2a3a'}
            onClick={()=>{setDetail(fac);setDetailTab('info');}}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12 }}>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:15,color:'#e8edf2',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{fac.name}</div>
                <div style={{ fontSize:11,color:'#7090a0',marginTop:3,fontFamily:"'IBM Plex Mono',monospace" }}>{fac.code} · {fac.type}</div>
              </div>
              <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6,flexShrink:0,marginLeft:10 }}>
                <Chip label={fac.status}/>
                {perms.canManageFacilities&&<button className="btn" style={{ background:'#1a2a3a',color:'#a0b4c0',padding:'4px 10px',fontSize:11 }} onClick={e=>{e.stopPropagation();setForm({...fac});setModal(fac);}}>Edit</button>}
              </div>
            </div>
            <div style={{ fontSize:12,color:'#a0b4c0',marginBottom:14 }}>📍 {fac.location||'No address set'}</div>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:12 }}>
              {[['Assets',facAssets.length,'#00e5a0'],['Available',available,'#5b9cf6'],['Antennas',`${onlineAnts}/${facAnts.length}`,'#b89de8'],['In Use',facAssets.filter(a=>a.status==='In Use').length,'#f5a623']].map(([k,v,c])=>(
                <div key={k} style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'8px 6px',textAlign:'center' }}>
                  <div style={{ fontSize:15,fontWeight:800,color:c,fontFamily:"'Syne',sans-serif" }}>{v}</div>
                  <div style={{ fontSize:9,color:'#7090a0',marginTop:2,textTransform:'uppercase',letterSpacing:'0.5px' }}>{k}</div>
                </div>
              ))}
            </div>
            <div>
              <div style={{ display:'flex',justifyContent:'space-between',fontSize:10,color:'#7090a0',marginBottom:4 }}>
                <span>Antenna health</span><span style={{ fontFamily:"'IBM Plex Mono',monospace" }}>{pct}%</span>
              </div>
              <div style={{ height:3,background:'#1a2a3a',borderRadius:2 }}>
                <div style={{ height:'100%',width:`${pct}%`,background:'linear-gradient(90deg,#00e5a0,#0a9e6e)',borderRadius:2,transition:'width .4s' }}/>
              </div>
            </div>
            {fac.manager&&<div style={{ marginTop:10,fontSize:11,color:'#7090a0' }}>👤 {fac.manager} {fac.contact&&`· ${fac.contact}`}</div>}
          </div>
        );
      })}
    </div>

    {/* Detail Modal */}
    {detail&&<Modal title={detail.name} sub={`${detail.code} · ${detail.type}`} onClose={()=>setDetail(null)} wide>
      <div style={{ display:'flex',gap:2,borderBottom:'1px solid #1a2a3a',marginBottom:18 }}>
        {['info','antennas','assets','floor map'].map(t=><button key={t} className="tab-btn" onClick={()=>setDetailTab(t)} style={{ color:detailTab===t?'#00e5a0':'#7090a0',borderBottom:detailTab===t?'2px solid #00e5a0':'2px solid transparent',textTransform:'capitalize' }}>
          {t}
          {t==='antennas'&&<span style={{ marginLeft:5,fontSize:10,background:'#1a2a3a',borderRadius:8,padding:'1px 6px',color:'#a0b4c0' }}>{antennas.filter(a=>String(a.facility_id)===String(detail.id)).length}</span>}
          {t==='assets'&&<span style={{ marginLeft:5,fontSize:10,background:'#1a2a3a',borderRadius:8,padding:'1px 6px',color:'#a0b4c0' }}>{assets.filter(a=>a.currentFacility===detail.id||a.facility_id===detail.id).length}</span>}
        </button>)}
      </div>

      {detailTab==='info'&&<div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:18 }}>
          {[['Facility ID',detail.id],['Code',detail.code],['Type',detail.type],['Status',detail.status],['Location',detail.location],['Manager',detail.manager],['Contact',detail.contact],['Total Assets',assets.filter(a=>a.currentFacility===detail.id||a.facility_id===detail.id).length],['Online Antennas',`${antennas.filter(a=>String(a.facility_id)===String(detail.id)&&isAntennaReallyOnline(a)).length} / ${antennas.filter(a=>String(a.facility_id)===String(detail.id)).length}`],['Available Assets',assets.filter(a=>a.facility_id===detail.id&&a.status==='Available').length]].map(([k,v])=>(
            <div key={k} style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'10px 14px' }}>
              <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',marginBottom:4 }}>{k}</div>
              <div style={{ fontSize:13,color:'#c8d6e0',fontFamily:['Facility ID','Code'].includes(k)?"'IBM Plex Mono',monospace":'inherit' }}>{String(v)||'—'}</div>
            </div>
          ))}
        </div>
        {perms.canConfig&&<div style={{ display:'flex',gap:10 }}>
          <button className="btn" style={{ flex:1,background:'#e0575720',border:'1px solid #e0575740',color:'#e05757',fontSize:12 }} onClick={()=>deleteFacility(detail)}>🗑 Delete</button>
          <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={()=>{setDetail(null);setForm({...detail});setModal(detail);}}>✏ Edit</button>
        </div>}
      </div>}

      {detailTab==='antennas'&&<BridgeAntennasTab
      detail={detail}
      antennas={antennas}
      bridgeReaders={bridgeReaders}
      bridgeUrl={bridgeUrl}
      fetchBridgeReaders={fetchBridgeReaders}
      onAddAntenna={(fac) => { setAntModal('add'); setAntForm({ facility_id:fac.id, facility_name:fac.name, location:'', antenna_name:'', status:'Online', reads:0, bridgeIp:'', bridgePort:9090, antennaPort:1 }); }}
      onEditAntenna={(ant) => { setAntModal(ant); setAntForm({ location:ant.location, antenna_name:ant.antenna_name||'', status:ant.status, bridgeIp:ant.bridge_ip||'', bridgePort:ant.bridge_port||9090, antennaPort:ant.antenna_port||1 }); }}
      onDeleteAntenna={deleteAntenna}
      saving={saving}
    />}
  {detailTab==='_antennas_old_disabled'&&<div>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}>
          <div style={{ fontSize:11,color:'#7090a0' }}>{antennas.filter(a=>String(a.facility_id)===String(detail.id)).length} antennas · {antennas.filter(a=>String(a.facility_id)===String(detail.id)&&isAntennaReallyOnline(a)).length} online</div>
          <button className="btn" style={{ background:'#00e5a0',color:'#060b10',fontSize:12,padding:'6px 14px' }}
            onClick={()=>{ setAntModal('add'); setAntForm({ facility_id:detail.id, facility_name:detail.name, location:'', status:'Online', reads:0 }); }}>
            + Add Antenna
          </button>
        </div>
        {antennas.filter(a=>String(a.facility_id)===String(detail.id)).map(ant=>(
          <div key={ant.id} style={{ display:'flex',alignItems:'center',gap:14,padding:'12px 14px',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,marginBottom:8 }}>
            <div style={{ width:10,height:10,borderRadius:'50%',background:isAntennaReallyOnline(ant)?'#00e5a0':'#e05757',flexShrink:0,
              boxShadow:isAntennaReallyOnline(ant)?'0 0 6px #00e5a0':'none' }}/>
            <div style={{ flex:1,minWidth:0 }}>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:'#5b9cf6',fontWeight:600 }}>
                ANT{ant.antenna_port} {ant.antenna_name && ant.antenna_name !== `ANT${ant.antenna_port}` ? `· ${ant.antenna_name}` : ''}
              </div>
              <div style={{ fontSize:11,color:'#7090a0',marginTop:2 }}>
                📍 {ant.location} · {(ant.reads||0).toLocaleString()} reads
                {ant.last_ping && <span style={{ marginLeft:8, color: isAntennaReallyOnline(ant)?'#00e5a080':'#7090a0' }}>· last ping {pingAge(ant.last_ping, now)}</span>}
              </div>
            </div>
            <Chip label={isAntennaReallyOnline(ant) ? 'Online' : 'Offline'}/>
            <div style={{ display:'flex',gap:6 }}>
              <button className="btn" style={{ background:'#1a2a3a',color:'#c8d6e0',fontSize:11,padding:'5px 10px' }}
                onClick={()=>{ setAntModal(ant); setAntForm({ location:ant.location, antenna_name:ant.antenna_name||'', status:ant.status, bridgeIp:ant.bridge_ip||'', bridgePort:ant.bridge_port||9090, antennaPort:ant.antenna_port||1 }); }}>✏</button>
              <button className="btn" style={{ background:'#e0575720',border:'1px solid #e0575740',color:'#e05757',fontSize:11,padding:'5px 10px' }}
                onClick={()=>deleteAntenna(ant)}>🗑</button>
            </div>
          </div>
        ))}
        {antennas.filter(a=>String(a.facility_id)===String(detail.id)).length===0&&<div style={{ textAlign:'center',color:'#7090a0',padding:'30px 0' }}>No antennas configured. Add one above.</div>}
      </div>}

      {detailTab==='assets'&&<div>
        {assets.filter(a=>a.currentFacility===detail.id||a.facility_id===detail.id).map(a=>(
          <div key={a.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,marginBottom:6 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13,fontWeight:600,color:'#e8edf2' }}>{a.name}</div>
              <div style={{ fontSize:11,color:'#7090a0',marginTop:2,fontFamily:"'IBM Plex Mono',monospace" }}>{a.inventory_number} · {a.type}</div>
            </div>
            <Chip label={a.status}/>
          </div>
        ))}
        {assets.filter(a=>a.currentFacility===detail.id||a.facility_id===detail.id).length===0&&<div style={{ textAlign:'center',color:'#7090a0',padding:'30px 0' }}>No assets at this facility</div>}
      </div>}
      {detailTab==='floor map'&&<FloorMapEditor facility={detail}/>}
    </Modal>}

    {/* Antenna Add/Edit Modal */}
    {antModal&&<Modal title={antModal==='add'?'Add Antenna':'Edit Antenna'} onClose={()=>setAntModal(null)}>
      <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
        <div>
          <Lbl>Antenna Name</Lbl>
          <input className="inp" style={{ width:'100%' }}
            value={antForm.antenna_name||''} placeholder={`e.g. ANT${antForm.antennaPort||1} — Entry Door`}
            onChange={e=>setAntForm(f=>({...f,antenna_name:e.target.value}))}/>
          <div style={{ fontSize:10,color:'#7090a0',marginTop:4 }}>Friendly label for this antenna port (defaults to ANT{antForm.antennaPort||1})</div>
        </div>
        <div>
          <Lbl>Location / Zone *</Lbl>
          <input className="inp" style={{ width:'100%' }} list="antenna-locs"
            value={antForm.location||''} placeholder="e.g. Main Entrance, Zone A, Loading Bay…"
            onChange={e=>setAntForm(f=>({...f,location:e.target.value}))}/>
          <datalist id="antenna-locs">
            {ANTENNA_LOCS.map(l=><option key={l} value={l}/>)}
          </datalist>
          <div style={{ fontSize:10,color:'#7090a0',marginTop:4 }}>Type freely or pick a suggestion</div>
        </div>
        <div>
          <Lbl>Status</Lbl>
          <select className="sel" style={{ width:'100%' }} value={antForm.status||'Online'} onChange={e=>setAntForm(f=>({...f,status:e.target.value}))}>
            <option>Online</option><option>Offline</option><option>Maintenance</option>
          </select>
        </div>

        <div style={{ padding:'12px 14px',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8 }}>
          <div style={{ fontSize:11,color:'#5b9cf6',fontWeight:700,marginBottom:10 }}>📡 RFID Bridge Mapping</div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10 }}>
            <div>
              <Lbl>Reader IP</Lbl>
              <input className="inp" style={{ width:'100%',fontFamily:"'IBM Plex Mono',monospace",fontSize:11 }}
                value={antForm.bridgeIp||''} placeholder="192.168.1.201"
                onChange={e=>setAntForm(f=>({...f,bridgeIp:e.target.value}))}/>
            </div>
            <div>
              <Lbl>Port</Lbl>
              <input className="inp" style={{ width:'100%',fontFamily:"'IBM Plex Mono',monospace",fontSize:11 }}
                type="number" value={antForm.bridgePort||9090}
                onChange={e=>setAntForm(f=>({...f,bridgePort:parseInt(e.target.value)}))}/>
            </div>
            <div>
              <Lbl>Antenna Port</Lbl>
              <select className="sel" style={{ width:'100%' }} value={antForm.antennaPort||1}
                onChange={e=>setAntForm(f=>({...f,antennaPort:parseInt(e.target.value)}))}>
                {[1,2,3,4,5,6,7,8].map(p => {
                  const existing = antennas.find(a =>
                    String(a.facility_id) === String(antForm.facility_id) &&
                    parseInt(a.antenna_port) === p &&
                    (antModal === 'add' || a.id !== antModal?.id)
                  );
                  return <option key={p} value={p}>
                    ANT {p}{existing ? ` — ${existing.location}` : ''}
                  </option>;
                })}
              </select>
            </div>
          </div>
          {bridgeReaders.length > 0 && (
            <div style={{ marginTop:10 }}>
              <Lbl>Or pick from connected readers</Lbl>
              <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                {bridgeReaders.filter(r=>r.connected).map(r=>(
                  <button key={r.key} className="btn" style={{ fontSize:10,padding:'3px 10px',
                    background:'#00e5a010',border:'1px solid #00e5a030',color:'#00e5a0' }}
                    onClick={()=>setAntForm(f=>({...f,bridgeIp:r.ip,bridgePort:r.port}))}>
                    {r.name} ({r.ip})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{ display:'flex',gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setAntModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={saveAntenna} disabled={saving||!antForm.location}>
          {saving?'Saving…':antModal==='add'?'Add Antenna':'Save Changes'}
        </button>
      </div>
    </Modal>}

    {/* Add/Edit Facility Modal */}
    {modal&&<Modal title={modal==='add'?'Add New Facility':'Edit Facility'} onClose={()=>setModal(null)}>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14 }}>
        <div style={{ gridColumn:'1/-1' }}><Lbl>Facility Name *</Lbl><input className="inp" value={form.name||''} placeholder="e.g. Subang Warehouse 2" onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div>
        <div><Lbl>Facility Code *</Lbl><input className="inp" value={form.code||''} placeholder="e.g. SBH2" onChange={e=>setForm(f=>({...f,code:e.target.value.toUpperCase()}))}/></div>
        <div><Lbl>Type</Lbl><select className="sel" style={{ width:'100%' }} value={form.type||'Warehouse'} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>{FACILITY_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
        <div style={{ gridColumn:'1/-1' }}><Lbl>Location / Address</Lbl><input className="inp" value={form.location||''} placeholder="Full address" onChange={e=>setForm(f=>({...f,location:e.target.value}))}/></div>
        <div><Lbl>Manager Name</Lbl><input className="inp" value={form.manager||''} onChange={e=>setForm(f=>({...f,manager:e.target.value}))}/></div>
        <div><Lbl>Contact</Lbl><input className="inp" value={form.contact||''} placeholder="Phone or email" onChange={e=>setForm(f=>({...f,contact:e.target.value}))}/></div>
        {modal==='add'&&<div><Lbl>No. of RFID Antennas</Lbl><input className="inp" type="number" min={1} max={20} value={form.antennas_count||2} onChange={e=>setForm(f=>({...f,antennas_count:e.target.value}))}/></div>}
        <div><Lbl>Status</Lbl><select className="sel" style={{ width:'100%' }} value={form.status||'Active'} onChange={e=>setForm(f=>({...f,status:e.target.value}))}><option>Active</option><option>Inactive</option></select></div>
      </div>
      <div style={{ display:'flex',gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={modal==='add'?saveAdd:saveEdit} disabled={saving}>{saving?'Saving…':modal==='add'?'Add Facility':'Save Changes'}</button>
      </div>
    </Modal>}
  </div>;
}

// ── Floor Map Editor ────────────────────────────────────────────────
const FM_TOOLS = [
  { id:'select',  icon:'↖', label:'Select'  },
  { id:'wall',    icon:'▬', label:'Wall'    },
  { id:'room',    icon:'⬜', label:'Room'    },
  { id:'door',    icon:'🚪', label:'Door'    },
  { id:'antenna', icon:'📡', label:'Antenna' },
  { id:'text',    icon:'T',  label:'Label'   },
];
const FM_COLORS = ['#1a2a3a','#2a3f52','#00e5a0','#5b9cf6','#f5a623','#e05757','#b89de8','#a0b4c0'];

function FloorMapEditor({ facility }) {
  const { floorMaps, saveFloorMap, antennas } = useApp();
  const svgRef  = useRef(null);
  const fileRef = useRef(null);

  const [tool,      setTool]      = useState('select');
  const [color,     setColor]     = useState('#2a3f52');
  const [shapes,    setShapes]    = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [drawing,   setDrawing]   = useState(null);
  const [bgImage,   setBgImage]   = useState(null);
  const [bgOpacity, setBgOpacity] = useState(0.4);
  const [savedMsg,  setSavedMsg]  = useState(false);

  // Load saved map
  useEffect(() => {
    const saved = floorMaps?.[facility.id];
    if (!saved) return;
    if (saved.shapes)              setShapes(saved.shapes);
    if (saved.bgImage)             setBgImage(saved.bgImage);
    if (saved.bgOpacity != null)   setBgOpacity(saved.bgOpacity);
  }, [facility.id]);

  const getPos = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: Math.round(e.clientX - rect.left), y: Math.round(e.clientY - rect.top) };
  };

  const onMouseDown = (e) => {
    const pos = getPos(e);
    if (tool === 'select') {
      const hit = [...shapes].reverse().find(s => {
        if (s.type === 'wall' || s.type === 'door') {
          const dx = s.x2-s.x1, dy = s.y2-s.y1;
          const len = Math.sqrt(dx*dx + dy*dy) || 1;
          const t = Math.max(0, Math.min(1, ((pos.x-s.x1)*dx + (pos.y-s.y1)*dy) / (len*len)));
          const px = s.x1+t*dx - pos.x, py = s.y1+t*dy - pos.y;
          return Math.sqrt(px*px + py*py) < 10;
        }
        return pos.x >= s.x && pos.y >= s.y && pos.x <= s.x+(s.w||32) && pos.y <= s.y+(s.h||32);
      });
      setSelected(hit ? hit.id : null);
      return;
    }
    if (tool === 'text') {
      const label = prompt('Enter label:');
      if (!label) return;
      setShapes(prev => [...prev, { id: Date.now(), type:'text', x:pos.x, y:pos.y, label, color }]);
      return;
    }
    if (tool === 'antenna') {
      setShapes(prev => [...prev, { id: Date.now(), type:'antenna', x:pos.x-16, y:pos.y-16, w:32, h:32, color }]);
      return;
    }
    setDrawing({ id: Date.now(), type:tool, x1:pos.x, y1:pos.y, x2:pos.x, y2:pos.y, x:pos.x, y:pos.y, w:0, h:0, color });
  };

  const onMouseMove = (e) => {
    if (!drawing) return;
    const pos = getPos(e);
    if (drawing.type === 'wall' || drawing.type === 'door') {
      setDrawing(d => ({ ...d, x2: pos.x, y2: pos.y }));
    } else {
      setDrawing(d => ({ ...d, w: pos.x - d.x, h: pos.y - d.y }));
    }
  };

  const onMouseUp = () => {
    if (!drawing) return;
    setShapes(prev => [...prev, drawing]);
    setDrawing(null);
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setBgImage(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSave = async () => {
    await saveFloorMap(facility.id, { shapes, bgImage, bgOpacity });
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2000);
  };

  const renderShape = (s) => {
    const sel = s.id === selected;
    switch (s.type) {
      case 'wall':
        return <line key={s.id} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
          stroke={s.color} strokeWidth={sel ? 5 : 3} strokeLinecap="round"
          style={{ cursor:'pointer' }} onClick={() => setSelected(s.id)} />;
      case 'door':
        return <line key={s.id} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
          stroke={s.color} strokeWidth={sel ? 4 : 2} strokeDasharray="8 4" strokeLinecap="round"
          style={{ cursor:'pointer' }} onClick={() => setSelected(s.id)} />;
      case 'room':
        return <rect key={s.id}
          x={Math.min(s.x, s.x + s.w)} y={Math.min(s.y, s.y + s.h)}
          width={Math.abs(s.w || 0)} height={Math.abs(s.h || 0)}
          fill={s.color + '22'} stroke={s.color} strokeWidth={sel ? 2 : 1} rx={4}
          style={{ cursor:'pointer' }} onClick={() => setSelected(s.id)} />;
      case 'antenna':
        return (
          <g key={s.id} style={{ cursor:'pointer' }} onClick={() => setSelected(s.id)}>
            <rect x={s.x} y={s.y} width={32} height={32} rx={6}
              fill="#00e5a020" stroke={sel ? '#00e5a0' : '#00e5a050'} strokeWidth={sel ? 2 : 1} />
            <text x={s.x+16} y={s.y+21} textAnchor="middle" fontSize={16}>📡</text>
          </g>
        );
      case 'text':
        return <text key={s.id} x={s.x} y={s.y} fill={s.color} fontSize={13}
          fontFamily="monospace" style={{ cursor:'pointer', userSelect:'none' }}
          onClick={() => setSelected(s.id)}>{s.label}</text>;
      default:
        return null;
    }
  };

  const facAntennas = (antennas || []).filter(a => a.facility_id === facility.id);
  const hasPlacedAnts = shapes.some(s => s.type === 'antenna');

  const hint = {
    select:  'Click a shape to select · Delete button to remove',
    wall:    'Drag to draw a wall',
    room:    'Drag to draw a room / zone',
    door:    'Drag to mark a door or opening',
    antenna: 'Click to place an antenna marker',
    text:    'Click to place a text label',
  }[tool];

  return (
    <div>
      {/* Row 1 — drawing tools + color picker */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:4 }}>
          {FM_TOOLS.map(t => (
            <button key={t.id} className="btn" title={t.label} onClick={() => setTool(t.id)}
              style={{ padding:'6px 10px', fontSize:12,
                background: tool===t.id ? '#00e5a020' : '#1a2a3a',
                border: `1px solid ${tool===t.id ? '#00e5a060' : '#2a3f52'}`,
                color: tool===t.id ? '#00e5a0' : '#a0b4c0' }}>
              {t.icon}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:4 }}>
          {FM_COLORS.map(c => (
            <div key={c} onClick={() => setColor(c)}
              style={{ width:20, height:20, borderRadius:4, background:c, cursor:'pointer',
                border: color===c ? '2px solid #00e5a0' : '2px solid transparent' }} />
          ))}
        </div>
      </div>

      {/* Row 2 — image upload + actions */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, flexWrap:'wrap' }}>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg"
          style={{ display:'none' }} onChange={handleUpload} />
        <button className="btn" onClick={() => fileRef.current.click()}
          style={{ background:'#1a2a3a', color:'#a0b4c0', fontSize:12, padding:'6px 12px' }}>
          🖼 {bgImage ? 'Change Floor Plan' : 'Upload Floor Plan'}
        </button>
        {bgImage && (
          <>
            <span style={{ fontSize:11, color:'#7090a0' }}>Opacity</span>
            <input type="range" min="0.05" max="1" step="0.05" value={bgOpacity}
              onChange={e => setBgOpacity(parseFloat(e.target.value))}
              style={{ width:80, accentColor:'#00e5a0' }} />
            <span style={{ fontSize:11, color:'#a0b4c0', minWidth:28 }}>{Math.round(bgOpacity*100)}%</span>
            <button className="btn" onClick={() => setBgImage(null)}
              style={{ background:'#e0575720', border:'1px solid #e0575740', color:'#e05757', fontSize:11, padding:'4px 8px' }}>
              ✕ Remove
            </button>
          </>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          {selected && (
            <button className="btn" onClick={() => { setShapes(p => p.filter(s => s.id !== selected)); setSelected(null); }}
              style={{ background:'#e0575720', border:'1px solid #e0575740', color:'#e05757', fontSize:12, padding:'6px 12px' }}>
              🗑 Delete
            </button>
          )}
          <button className="btn" onClick={() => setShapes([])}
            style={{ background:'#1a2a3a', color:'#a0b4c0', fontSize:12, padding:'6px 12px' }}>
            ↺ Clear
          </button>
          <button className="btn" onClick={handleSave}
            style={{ background:'#00e5a0', color:'#060b10', fontSize:12, padding:'6px 16px' }}>
            {savedMsg ? '✓ Saved' : '💾 Save Map'}
          </button>
        </div>
      </div>

      {/* Hint bar */}
      <div style={{ fontSize:11, color:'#7090a0', marginBottom:8, fontFamily:'monospace' }}>{hint}</div>

      {/* SVG canvas */}
      <div style={{ border:'1px solid #1a2a3a', borderRadius:10, overflow:'hidden',
        cursor: tool==='select' ? 'default' : 'crosshair' }}>
        <svg ref={svgRef} width="100%" height="460" style={{ background:'#060b10', display:'block' }}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
          <defs>
            <pattern id="fmap-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#1a2a3a" strokeWidth="0.5" />
            </pattern>
          </defs>
          {/* Grid */}
          <rect width="100%" height="100%" fill="url(#fmap-grid)" />
          {/* Background image */}
          {bgImage && (
            <image href={bgImage} x="0" y="0" width="100%" height="100%"
              opacity={bgOpacity} preserveAspectRatio="xMidYMid meet" />
          )}
          {/* Saved shapes */}
          {shapes.map(s => renderShape(s))}
          {/* Shape being drawn */}
          {drawing && renderShape(drawing)}
          {/* Default antenna positions from DB (if none placed yet) */}
          {!hasPlacedAnts && facAntennas.map((ant, i) => (
            <g key={ant.id}>
              <rect x={30+i*70} y={20} width={32} height={32} rx={6}
                fill="#00e5a015" stroke="#00e5a040" strokeWidth={1} />
              <text x={46+i*70} y={41} textAnchor="middle" fontSize={14}>📡</text>
              <text x={46+i*70} y={63} textAnchor="middle" fontSize={9} fill="#7090a0">
                {(ant.location || '').slice(0, 10)}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <div style={{ fontSize:11, color:'#7090a0', marginTop:6 }}>
        Upload a PNG/JPG floor plan as background · Draw walls and zones on top · Save to persist
      </div>
    </div>
  );
}

/* ── Bridge Antennas Tab ──────────────────────────────────────────────────── */
function BridgeAntennasTab({ detail, antennas, bridgeReaders, bridgeUrl, fetchBridgeReaders, onAddAntenna, onEditAntenna, onDeleteAntenna }) {
  const facAnts    = antennas.filter(a => String(a.facility_id) === String(detail.id));
  const [bridgeStatus, setBridgeStatus] = useState(null);

  useEffect(() => {
    fetchBridgeReaders();
  }, []);

  // Check which bridge readers are mapped to this facility's antennas
  const mappedIps = new Set(facAnts.map(a => a.bridge_ip).filter(Boolean));

  return (
    <div>
      {/* Bridge Status Bar */}
      <div style={{ marginBottom:12, padding:'10px 14px', background:'#060b10',
        border:`1px solid ${bridgeReaders.length > 0 ? '#5b9cf630' : '#1a2a3a'}`, borderRadius:8,
        display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, flex:1 }}>
          <div style={{ width:7, height:7, borderRadius:'50%', background: bridgeReaders.length > 0 ? '#00e5a0' : '#e05757' }}/>
          <span style={{ fontSize:12, fontWeight:700, color: bridgeReaders.length > 0 ? '#00e5a0' : '#a0b4c0' }}>
            {bridgeReaders.length > 0 ? `Bridge: ${bridgeReaders.filter(r=>r.connected).length}/${bridgeReaders.length} readers online` : 'Bridge offline'}
          </span>
        </div>
        <button style={{ background:'none', border:'none', color:'#7090a0', cursor:'pointer', fontSize:12 }}
          onClick={fetchBridgeReaders}>↻ Refresh</button>
      </div>

      {/* Connected readers available to map */}
      {bridgeReaders.filter(r => r.connected).length > 0 && (
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:10, color:'#7090a0', textTransform:'uppercase', letterSpacing:'1px', marginBottom:6 }}>
            Available Readers
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {bridgeReaders.filter(r => r.connected).map(r => (
              <div key={r.key} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 12px',
                background: mappedIps.has(r.ip) ? '#00e5a010' : '#1a2a3a',
                border: `1px solid ${mappedIps.has(r.ip) ? '#00e5a040' : '#2a3a4a'}`,
                borderRadius:8, fontSize:11 }}>
                <div style={{ width:6, height:6, borderRadius:'50%', background:'#00e5a0' }}/>
                <span style={{ color:'#e8edf2', fontWeight:600 }}>{r.name}</span>
                <span style={{ color:'#7090a0', fontFamily:"'IBM Plex Mono',monospace" }}>{r.ip}:{r.port}</span>
                <span style={{ color:'#7090a0' }}>{r.tagsRead || 0} reads</span>
                {mappedIps.has(r.ip) && <span style={{ color:'#00e5a0', fontSize:10, fontWeight:700 }}>✓ Mapped</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Antennas List */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontSize:11, color:'#7090a0' }}>
          {facAnts.length} antenna{facAnts.length !== 1 ? 's' : ''} · {facAnts.filter(a => a.status === 'Online').length} online
        </div>
        <button className="btn" style={{ background:'#00e5a0', color:'#060b10', fontSize:12, padding:'6px 14px' }}
          onClick={() => onAddAntenna(detail)}>+ Add Antenna</button>
      </div>

      {facAnts.map(ant => {
        const bridgeReader  = bridgeReaders.find(r => r.ip === ant.bridge_ip);
        // Use bridge real-time status if available, else fall back to DB status
        const bridgeActive  = bridgeReader?.connected;
        const portHasReads  = bridgeReader?.portCounts?.[ant.antenna_port] > 0;
        const realStatus    = bridgeActive
          ? (portHasReads ? 'Online' : 'Offline')
          : ant.status;
        const statusColor   = realStatus === 'Online' ? '#00e5a0' : '#e05757';
        const fromBridge    = bridgeActive;
        return (
          <div key={ant.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
            background:'#060b10', border:`1px solid ${realStatus==='Online'?'#00e5a020':'#e0575720'}`, borderRadius:8, marginBottom:8 }}>
            <div style={{ width:10, height:10, borderRadius:'50%', flexShrink:0,
              background: statusColor }}/>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:6,
                  background:'#00e5a018', border:'1px solid #00e5a030', color:'#00e5a0',
                  fontFamily:"'IBM Plex Mono',monospace", flexShrink:0 }}>
                  ANT {ant.antenna_port || '?'}
                </span>
                <div style={{ fontSize:13, fontWeight:700, color:'#e8edf2' }}>{ant.location}</div>
                {fromBridge && (
                  <span style={{ fontSize:10, padding:'1px 6px', borderRadius:4,
                    background: realStatus==='Online' ? '#00e5a018' : '#e0575718',
                    color: realStatus==='Online' ? '#00e5a0' : '#e05757',
                    border: `1px solid ${realStatus==='Online'?'#00e5a030':'#e0575730'}` }}>
                    {realStatus==='Online' ? '● Live' : '○ No signal'}
                  </span>
                )}
              </div>
              <div style={{ display:'flex', gap:10, fontSize:11, color:'#7090a0', marginTop:2, flexWrap:'wrap' }}>
                <span>{(ant.reads || 0).toLocaleString()} reads</span>
                {ant.bridge_ip && (
                  <>
                    <span style={{ color:'#5b9cf6', fontFamily:"'IBM Plex Mono',monospace" }}>
                      {ant.bridge_ip}:{ant.bridge_port || 9090}
                    </span>
                    <span style={{ color:'#a0b4c0' }}>ANT {ant.antenna_port || 1}</span>
                    {bridgeReader ? (
                      <span style={{ color: bridgeReader.connected ? '#00e5a0' : '#e05757', fontWeight:700 }}>
                        {bridgeReader.connected ? `● Online (${bridgeReader.tagsRead || 0} tags)` : '○ Reader offline'}
                      </span>
                    ) : (
                      <span style={{ color:'#a0b4c0' }}>Bridge not connected</span>
                    )}
                  </>
                )}
                {!ant.bridge_ip && <span style={{ color:'#f5a623' }}>⚠ No bridge mapped</span>}
              </div>
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <button className="btn" style={{ background:'#1a2a3a', color:'#c8d6e0', fontSize:11, padding:'5px 10px' }}
                onClick={() => onEditAntenna(ant)}>✏</button>
              <button className="btn" style={{ background:'#e0575720', border:'1px solid #e0575740', color:'#e05757', fontSize:11, padding:'5px 10px' }}
                onClick={() => onDeleteAntenna(ant)}>🗑</button>
            </div>
          </div>
        );
      })}

      {facAnts.length === 0 && (
        <div style={{ textAlign:'center', color:'#7090a0', padding:'40px 0' }}>
          <div style={{ fontSize:28, marginBottom:10 }}>📡</div>
          No antennas configured for this facility. Add one above.
        </div>
      )}
    </div>
  );
}
